/**
 * The bell and its panel. Each row can be opened, drafted against, snoozed for
 * three days, or dismissed for good.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import Icon from '../lib/icons.jsx';
import { Avatar } from './Primitives.jsx';

export default function AlertsDropdown({ badge }) {
  const {
    alerts, astate, markAlertRead, snoozeAlert, dismissAlert, markAllAlertsRead,
    setSheet, openDraft, fmtMoney
  } = useApp();

  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const bellRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = e => {
      if (panelRef.current?.contains(e.target) || bellRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  /** Open whichever record the alert points at. */
  const openRecord = alert => {
    setOpen(false);
    markAlertRead(alert.id);
    if (alert.kind === 'deal') setSheet({ kind: 'deal', id: alert.cid });
    else if (alert.kind === 'act') setSheet({ kind: 'activity', id: alert.cid });
    /* A booking is a row on its own screen, not a sheet: go there with it open. */
    else if (alert.kind === 'demo') navigate('/demo-bookings', { state: { open: alert.cid } });
    else setSheet({ kind: 'client', id: alert.cid });
  };

  return (
    <>
      <button
        type="button"
        className="iconbtn"
        aria-label="Alerts"
        title="Alerts"
        ref={bellRef}
        onClick={() => setOpen(o => !o)}
      >
        <Icon name="bell" />
        {badge ? <span className="dot">{badge}</span> : null}
      </button>

      <div className={`drop${open ? ' open' : ''}`} ref={panelRef}>
        <div className="dh">
          Alerts
          {alerts.length ? <span className="pill-n">{alerts.length}</span> : null}
          <div className="spacer" style={{ flex: 1 }} />
          <button type="button" className="btn sm" onClick={markAllAlertsRead}>Mark all read</button>
        </div>

        {alerts.length ? (
          <>
            <div className="alist">
              {alerts.slice(0, 25).map(a => (
                <div key={a.id} className={`al${astate.read[a.id] ? '' : ' unread'}`}>
                  <div className={`sev s${a.sev}`} style={{ marginTop: 6 }} />
                  <Avatar name={a.client} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <b>{a.client}</b>
                    <div className="why">{a.txt}{a.value ? ` · ${fmtMoney(a.value)}/mo` : ''}</div>
                    <div className="row">
                      <button type="button" className="btn sm" onClick={() => openRecord(a)}>Open</button>
                      {/* A booking has no record of its own to draft to. */}
                      {a.kind !== 'demo' && a.kind !== 'act' ? (
                        <button
                          type="button"
                          className="btn sm"
                          onClick={() => {
                            markAlertRead(a.id);
                            setOpen(false);
                            openDraft({ kind: a.kind === 'deal' ? 'deal' : 'client', id: a.cid });
                          }}
                        >
                          Draft email
                        </button>
                      ) : null}
                      <button type="button" className="btn sm" onClick={() => snoozeAlert(a.id, 3)}>Snooze 3d</button>
                      <button type="button" className="btn sm" onClick={() => dismissAlert(a.id)}>Dismiss</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ padding: 12, borderTop: '1px solid var(--line)' }}>
              <button
                type="button"
                className="btn"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { setOpen(false); navigate('/needs-action'); }}
              >
                Open action list
              </button>
            </div>
          </>
        ) : (
          <div className="quiet">
            <b style={{ display: 'block', color: 'var(--ink)', marginBottom: 4 }}>All clear</b>
            Nothing needs chasing right now.
          </div>
        )}
      </div>
    </>
  );
}
